import { MenuProps } from "antd";

import { CiqIconBox } from "components/svg-icons";

import { ProjectContext } from "context/ProjectProvider";

import { isAccSubmittalProject } from "pages/subscription-settings/integrations-tab-new/utils";

import { ReactNode, useCallback, useContext, useMemo } from "react";

import { useHistory, useLocation, useParams } from "react-router-dom";

import {
  getSubmittalLogRoute,
  getSubmittalLogViewFromPath,
  SubmittalLogView
} from "pages/submittal-log/submittal-log-view";

import useSubmittalCategoryCounts from "../hooks/submittal-category-counts";

function SubmittalTypeMenuOption({
  icon,
  title,
  description,
  count,
  testId
}: {
  icon: ReactNode;
  title: string;
  description: string;
  count: number | undefined;
  testId: string;
}) {
  return (
    <div className="flex items-center space-x-3">
      {icon}

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between space-x-3">
          <div className="font-medium text-black text-opacity-90">{title}</div>

          {count !== undefined && (
            <span
              className="shrink-0 rounded-full border border-solid border-ciq-gray-300 bg-white px-2 text-xs leading-5 text-ciq-gray-700"
              data-testid={`${testId}-count`}
            >
              {count}
            </span>
          )}
        </div>

        <div className="text-ciq-gray-700 text-xs">{description}</div>
      </div>
    </div>
  );
}

function SubmittalUntrackedsTypeMenu() {
  const history = useHistory();

  const location = useLocation();

  const { projectId } = useParams() as { projectId: string };

  const { isProcoreProject, projectIntegrationConfig } =
    useContext(ProjectContext);

  const counts = useSubmittalCategoryCounts();

  const currentView = getSubmittalLogViewFromPath(location.pathname);

  const showVoidedOption = isAccSubmittalProject(projectIntegrationConfig);

  const showDeletedOption = !!isProcoreProject;

  const navigateToView = useCallback(
    (view: SubmittalLogView) => {
      history.push(getSubmittalLogRoute(projectId, view));
    },

    [history, projectId]
  );

  const selectedMenuKey = useMemo(() => {
    if (location.pathname.endsWith("/submittals-untracked")) {
      return "extracted_from_pdf";
    }

    if (currentView === "voided") {
      return "voided_submittals";
    }

    if (currentView === "deleted") {
      return "deleted_submittals";
    }

    return "submittal-untracked_log";
  }, [currentView, location.pathname]);

  const submittalUntrackedsTypeMenu: MenuProps = useMemo(
    () => ({
      className: "max-w-md",

      items: [
        {
          key: "submittal-untracked_log",

          onClick: () => navigateToView("default"),

          className: "px-4 py-2",

          label: (
            <SubmittalTypeMenuOption
              icon={<CiqIconBox feature="SUBMITTALS" size={42} padding={0} />}
              title="Submittal Log"
              description="Planned and tracked for Material procurement."
              count={counts.tracked}
              testId="submittal-type-menu-tracked"
            />
          )
        },

        {
          key: "extracted_from_pdf",

          onClick: () =>
            history.push(`/project/${projectId}/submittals-untracked`),

          className: "px-4 py-2",

          label: (
            <SubmittalTypeMenuOption
              icon={
                <CiqIconBox
                  feature="SUBMITTALS_UNTRACKED"
                  size={42}
                  padding={0}
                />
              }
              title="Submittals Not Tracked"
              description="Excluded from procurement planning."
              count={counts.untracked}
              testId="submittal-type-menu-untracked"
            />
          )
        },

        ...(showVoidedOption
          ? [
              {
                key: "voided_submittals",

                onClick: () => navigateToView("voided"),

                className: "px-4 py-2",

                label: (
                  <SubmittalTypeMenuOption
                    icon={
                      <CiqIconBox
                        feature="SUBMITTALS_VOIDED"
                        size={42}
                        padding={0}
                      />
                    }
                    title="Voided Submittals"
                    description="Voided in Autodesk Forma."
                    count={counts.voided}
                    testId="submittal-type-menu-voided"
                  />
                )
              }
            ]
          : []),

        ...(showDeletedOption
          ? [
              {
                key: "deleted_submittals",

                onClick: () => navigateToView("deleted"),

                className: "px-4 py-2",

                label: (
                  <SubmittalTypeMenuOption
                    icon={
                      <CiqIconBox
                        feature="SUBMITTALS_DELETED"
                        size={42}
                        padding={0}
                      />
                    }
                    title="Recycle Bin"
                    description="Deleted in Procore."
                    count={counts.deleted}
                    testId="submittal-type-menu-deleted"
                  />
                )
              }
            ]
          : [])
      ],

      selectedKeys: [selectedMenuKey]
    }),

    [
      counts,

      history,

      navigateToView,

      projectId,

      selectedMenuKey,

      showDeletedOption,

      showVoidedOption
    ]
  );

  return submittalUntrackedsTypeMenu;
}

export default SubmittalUntrackedsTypeMenu;
